/**
 * Student-facing copy of the cementing module panel, after the Cementing
 * Message Catalog (draft 6, 13 Oct 2026). Every message, number and button is
 * derived here from the status API; nothing is fetched.
 *
 * Voice: a subject expert talking to the student, active voice, "you", no
 * "I"/"we", no internal terms (L20, even pair, steady, strong, swing). Medium
 * speaks in accuracy (the average of the last two quizzes); Hard speaks in
 * mistakes. Every target is one number the student can check on the next
 * quiz: "Score 80% or more in Quiz 5" / "Make 4 mistakes or fewer in Quiz 5".
 * When one quiz blocks a pass, that quiz and its score are named, and so is
 * the quiz that replaces it. The filled button is the recommended one.
 */
import {
  attemptedSlots, barFor, CementingAttempt, CementingBar, CementingBarName, CementingCategory, CementingChoice, CementingMoment,
  levelOf, levelTone, moduleName, momentOfQuiz, PASSED_STATES, PillIcon, plural, questionsOf, resumeProgress, Tone
} from './cementing-status';

/** "Contact my mentor" target; empty falls back to the in-app chat or support mail (spec open item 6). */
export const CEMENTING_MENTOR_URL = '';

/** Support address when the chat cannot open (widget blocked, or Intercom refused the messenger). */
export const CEMENTING_SUPPORT_EMAIL = 'support@e-gmat.com';

/** Shown in the action box when "Contact my mentor" cannot open the chat. Not in the catalog yet. */
export const CEMENTING_CHAT_BLOCKED =
  'Our chat could not open just now. Please write to ' + CEMENTING_SUPPORT_EMAIL + ' and we will connect you with your mentor.';

/* ------------------------------------------------------------------------ */
/* Helpers                                                                   */
/* ------------------------------------------------------------------------ */

function isHard(c: CementingCategory): boolean { return c.difficulty === 'HARD'; }

function pct(score: number, max: number): number { return max <= 0 ? 0 : Math.round(score * 100 / max); }

function quiz(n: number | null | undefined): string { return 'Quiz ' + n; }

function mistakes(n: number): string { return plural(n, 'mistake'); }

function orFewer(n: number): string { return mistakes(n) + ' or fewer'; }

function lc(s: string): string { return s.charAt(0).toLowerCase() + s.slice(1); }

function barName(bar: CementingBarName): string { return bar === 'MASTERY' ? 'mastery' : 'the threshold'; }

/**
 * The latest pair of quizzes of a module as the numbers the copy uses. `max` is
 * the pair's own questions: legacy RC / MSR quizzes run 5 to 10, so a pair can
 * be 12 questions where the rules say 18.
 */
interface L20Numbers {
  score: number; max: number; percent: number; mistakes: number;
  prevNumber: number | null; prevScore: number | null; number: number | null; quizScore: number | null;
  prevQuestions: number; questions: number;
  pairEven: boolean | null; farBelowQuizNumber: number | null;
}

function l20Of(c: CementingCategory): L20Numbers | null {
  const l = c.summary.latestL20;
  if (!l) { return null; }
  return {
    score: l.score, max: l.l20Max || c.rules.l20Max, percent: l.percent, mistakes: l.mistakes,
    prevNumber: l.prevQuizNumber, prevScore: l.prevScore, number: l.quizNumber, quizScore: l.quizScore,
    prevQuestions: questionsOf(c, l.prevQuizNumber), questions: questionsOf(c, l.quizNumber),
    pairEven: l.pairEven, farBelowQuizNumber: l.farBelowQuizNumber
  };
}

function l20OfMoment(c: CementingCategory, m: CementingMoment): L20Numbers | null {
  if (m.l20 === null || m.l20 === undefined) { return l20Of(c); }
  const max = m.l20Max || c.rules.l20Max;
  return {
    score: m.l20, max, percent: pct(m.l20, max), mistakes: max - m.l20,
    prevNumber: m.prevQuizNumber, prevScore: m.prevScore, number: m.quizNumber, quizScore: m.score,
    prevQuestions: questionsOf(c, m.prevQuizNumber), questions: questionsOf(c, m.quizNumber),
    pairEven: m.pairEven, farBelowQuizNumber: m.farBelowQuizNumber
  };
}

/** "close" to a bar: Medium within 5 points of its percent, Hard 1–2 mistakes over its allowance. */
function closeTo(c: CementingCategory, l20: L20Numbers, bar: CementingBar): boolean {
  if (isHard(c)) {
    const over = l20.mistakes - barFor(bar, c.rules, l20.max).mistakesAllowed;
    return over >= 1 && over <= 2;
  }
  return l20.percent < bar.percent && l20.percent >= bar.percent - 5;
}

/** Mistakes over a bar's allowance for the pair's own questions (Hard): "1 mistake over". */
function overBy(c: CementingCategory, l20: L20Numbers, bar: CementingBar): number {
  return Math.max(0, l20.mistakes - barFor(bar, c.rules, l20.max).mistakesAllowed);
}

/** "You made {{10 mistakes}} in your last two quizzes" / "Your last two quizzes average {{65%}}". */
function lastTwo(c: CementingCategory, l20: L20Numbers, still: boolean = false): string {
  return isHard(c)
    ? 'You ' + (still ? 'still ' : '') + 'made {{' + mistakes(l20.mistakes) + '}} in your last two quizzes'
    : 'Your last two quizzes ' + (still ? 'still ' : '') + 'average {{' + l20.percent + '%}}';
}

/** "The threshold allows {{9}}." / "The threshold is {{70%}}." (and the same for mastery), for the pair shown when given. */
function barLine(c: CementingCategory, bar: CementingBarName, l20: L20Numbers | null = null): string {
  const b = barFor(bar === 'MASTERY' ? c.rules.masteryTarget : c.rules.threshold, c.rules, l20 ? l20.max : null);
  const name = bar === 'MASTERY' ? 'Mastery' : 'The threshold';
  return isHard(c) ? name + ' allows {{' + b.mistakesAllowed + '}}.' : name + (bar === 'MASTERY' ? ' needs {{' : ' is {{') + b.percent + '%}}.';
}

/** The one quiz of the pair too far below to count, when the pair is uneven. */
function lowQuiz(c: CementingCategory, l20: L20Numbers): { number: number; score: number } | null {
  if (l20.prevNumber === null || l20.number === null || l20.prevScore === null || l20.quizScore === null) { return null; }
  if (l20.farBelowQuizNumber === l20.number) { return { number: l20.number, score: l20.quizScore }; }
  if (l20.farBelowQuizNumber === l20.prevNumber) { return { number: l20.prevNumber, score: l20.prevScore }; }
  return l20.prevScore * l20.questions <= l20.quizScore * l20.prevQuestions
    ? { number: l20.prevNumber, score: l20.prevScore } : { number: l20.number, score: l20.quizScore };
}

/** The next-quiz target in the module's voice, from the server's target (spec 8.5). */
interface NextGoal {
  reachable: boolean;
  /** "Make {{4 mistakes}} or fewer" / "Score {{80%}} or more" / "Get every question right". */
  verb: string;
  /** verb + " in Quiz 5". */
  inQuiz: string;
}

function goal(c: CementingCategory, nextNumber: number, bar: CementingBarName): NextGoal {
  const t = c.summary.nextQuizTarget;
  const barVo = bar === 'MASTERY' ? c.rules.masteryTarget : c.rules.threshold;
  const q = t && t.questions ? t.questions : c.rules.questionsPerQuiz;
  const pair = t && t.kind === 'PAIR';
  let reachable = true;
  let verb: string;
  if (t && t.kind === 'OUT_OF_REACH') {
    reachable = false;
    verb = isHard(c) ? 'Make {{' + orFewer(t.perQuizMistakes) + '}}' : 'Score {{' + barVo.percent + '%}} or more';
  } else if (isHard(c)) {
    const n = pair && t.mistakesOrFewer !== null ? t.mistakesOrFewer : (t ? t.perQuizMistakes : barVo.perQuizMistakes);
    verb = n === 0 ? 'Get every question right' : 'Make {{' + orFewer(n) + '}}';
  } else {
    const score = pair && t.targetScore !== null ? t.targetScore : null;
    verb = score !== null && score >= q ? 'Get every question right' : 'Score {{' + (score === null ? barVo.percent : pct(score, q)) + '%}} or more';
  }
  return { reachable, verb, inQuiz: verb + ' in ' + quiz(nextNumber) };
}

/** Two-quiz target when the next quiz alone can't reach the bar: "Make {{4 mistakes}} or fewer in Quiz 3 and in Quiz 4". */
function twoQuizGoal(c: CementingCategory, nextNumber: number, bar: CementingBarName): string {
  const t = c.summary.nextQuizTarget;
  const barVo = bar === 'MASTERY' ? c.rules.masteryTarget : c.rules.threshold;
  const perQuiz = t ? t.perQuizMistakes : barVo.perQuizMistakes;
  return (isHard(c) ? 'Make {{' + orFewer(perQuiz) + '}}' : 'Score {{' + barVo.percent + '%}} or more')
    + ' in ' + quiz(nextNumber) + ' and in ' + quiz(nextNumber + 1);
}

/** Why the next quiz alone can't reach the bar: the previous quiz was too low. */
function tooLowLine(c: CementingCategory, prevNumber: number, prevScore: number, nextNumber: number): string {
  const q = questionsOf(c, prevNumber);
  const tail = ' Even a perfect ' + quiz(nextNumber) + ' can\'t make up for it. From ' + quiz(nextNumber + 1) + ', ' + quiz(prevNumber) + ' no longer counts.';
  return isHard(c)
    ? quiz(prevNumber) + ' had {{' + mistakes(q - prevScore) + '}}.' + tail
    : 'Your ' + quiz(prevNumber) + ' score ({{' + pct(prevScore, q) + '%}}) is too low.' + tail;
}

/** The previous counted quiz behind the next target. */
function prevOfTarget(c: CementingCategory, last: CementingAttempt | null): { number: number; score: number } | null {
  const t = c.summary.nextQuizTarget;
  const l20 = l20Of(c);
  const number = l20 && l20.number !== null ? l20.number : (last ? last.quizNumber : null);
  const score = t && t.prevScore !== null ? t.prevScore : (l20 && l20.quizScore !== null ? l20.quizScore : (last ? last.score : null));
  return number === null || score === null ? null : { number, score };
}

/** Uneven pair at a check-in: the total passes but one quiz is too low to count (C4 / K3 / M4 / E8). */
function unevenLine(c: CementingCategory, l20: L20Numbers, bar: CementingBarName, low: { number: number; score: number }, replacedBy: number | null): string {
  const q = questionsOf(c, low.number);
  const barVo = barFor(bar === 'MASTERY' ? c.rules.masteryTarget : c.rules.threshold, c.rules, l20.max);
  const replaces = replacedBy === null ? '' : ' ' + quiz(replacedBy) + ' replaces ' + quiz(low.number) + '.';
  return (isHard(c)
    ? 'Your last two quizzes have {{' + mistakes(l20.mistakes) + '}}, within the {{' + barVo.mistakesAllowed + '}} ' + (bar === 'MASTERY' ? 'mastery allows' : 'allowed')
      + '. But ' + quiz(low.number) + ' alone has {{' + (q - low.score) + '}}, too many to count.'
    : 'Your last two quizzes average {{' + l20.percent + '%}}. But your ' + quiz(low.number) + ' score ({{' + pct(low.score, q) + '%}}) is too low to count.')
    + replaces;
}

/* ------------------------------------------------------------------------ */
/* Level card pill and ring                                                  */
/* ------------------------------------------------------------------------ */

/** The "What is L20?" box of the level card (design boards FMV2 / FMV11). */
export interface LevelInfo {
  /** The explanation; {{ }} marks a value for the template. */
  intro: string;
  /** The student's own sum, once an L20 exists: "Quiz 4: 7/10" + "Quiz 5: 8/10" = "15/20 · 75%". */
  sum: { prev: string; cur: string; total: string } | null;
  /** Before the first L20: the two quizzes that will make it, e.g. "Quiz 2 + Quiz 3". */
  firstPair: string | null;
}

export interface LevelCardView {
  pill: { label: string; tone: Tone; strong: boolean; icon: PillIcon };
  percent: number | null;
  ringLabel: string;
  ringTone: Tone;
  thresholdPercent: number;
  masteryPercent: number;
  thresholdDotTone: Tone;
  masteryDotTone: Tone;
  info: LevelInfo;
}

export function deriveLevelCard(c: CementingCategory): LevelCardView {
  const { summary, rules } = c;
  const hard = isHard(c);
  const l20 = l20Of(c);
  const l20Level = l20 ? levelOf(l20.score, rules, l20.max) : null;
  const ringTone = levelTone(l20Level);
  let pill: LevelCardView['pill'];
  const waiting = summary.waitingQuizNumber !== null && summary.waitingQuizNumber !== undefined ? momentOfQuiz(c, summary.waitingQuizNumber) : null;
  switch (summary.status) {
    case 'DONE_MASTERY_MET':
      pill = { label: '✓ Mastery reached', tone: 'green', strong: true, icon: 'none' };
      break;
    case 'DONE_THRESHOLD_MET':
      pill = { label: '✓ Done: Threshold met', tone: 'amber', strong: true, icon: 'none' };
      break;
    case 'DONE_THRESHOLD_NOT_MET':
      pill = { label: 'Done: Threshold not met · talk to your mentor', tone: 'red', strong: true, icon: 'mentor' };
      break;
    case 'NOT_NEEDED':
      pill = { label: 'Not needed · you passed Hard', tone: 'grey', strong: true, icon: 'check' };
      break;
    case 'THRESHOLD_MET_GOING_FOR_MASTERY':
      if (waiting && waiting.moment === 'CELEBRATE_THRESHOLD') {
        pill = { label: '↗ Threshold met · go for mastery', tone: 'amber', strong: false, icon: 'none' };
      } else if (l20 && l20Level === 'BELOW_THRESHOLD') {
        pill = { label: '↗ Threshold met · going for mastery', tone: 'amber', strong: false, icon: 'none' };
      } else {
        const over = hard && l20 ? overBy(c, l20, rules.masteryTarget) : 0;
        pill = { label: '↗ Going for mastery' + (over > 0 ? ' · ' + mistakes(over) + ' over' : ''), tone: 'amber', strong: false, icon: 'none' };
      }
      break;
    default:
      if (summary.countedQuizzes === 0 && summary.abandonedQuizzes === 0) {
        pill = { label: 'Start with your warm-up', tone: 'blue', strong: false, icon: 'play' };
      } else if (!l20) {
        const next = c.nextQuiz ? c.nextQuiz.quizNumber : summary.countedQuizzes + summary.abandonedQuizzes + 1;
        pill = { label: 'Your score shows after ' + quiz(next + Math.max(0, 2 - summary.countedQuizzes)), tone: 'blue', strong: false, icon: 'play' };
      } else if (l20.pairEven === false && l20.score >= barFor(rules.threshold, rules, l20.max).score) {
        pill = { label: 'One quiz too low · one more quiz', tone: 'amber', strong: false, icon: 'none' };
      } else {
        const over = hard ? overBy(c, l20, rules.threshold) : 0;
        pill = { label: '↓ Below threshold' + (over > 0 ? ' · ' + mistakes(over) + ' over' : ''), tone: 'red', strong: false, icon: 'none' };
      }
  }
  const intro = 'Your accuracy on your {{last ' + (l20 ? l20.max : rules.l20Max) + ' questions}}: your 2 latest quizzes, first attempts only. Quiz 1 is a warm-up and doesn\'t count.';
  let sum: LevelInfo['sum'] = null;
  let firstPair: string | null = null;
  if (l20 && l20.prevScore !== null && l20.quizScore !== null) {
    sum = {
      prev: quiz(l20.prevNumber) + ': ' + l20.prevScore + '/' + l20.prevQuestions,
      cur: quiz(l20.number) + ': ' + l20.quizScore + '/' + l20.questions,
      total: l20.score + '/' + l20.max + ' · ' + l20.percent + '%'
    };
  } else if (!l20) {
    const next = c.nextQuiz ? c.nextQuiz.quizNumber : summary.countedQuizzes + summary.abandonedQuizzes + 1;
    const first = next + (summary.countedQuizzes === 0 ? 1 : 0);
    firstPair = quiz(first) + ' + ' + quiz(first + 1);
  }
  return {
    pill,
    percent: l20 ? l20.percent : null,
    ringLabel: 'Your L20',
    ringTone,
    thresholdPercent: rules.threshold.percent,
    masteryPercent: rules.masteryTarget.percent,
    thresholdDotTone: l20Level ? ringTone : 'grey',
    masteryDotTone: l20Level === 'MASTERY_MET' || summary.bestLevel === 'MASTERY_MET' ? 'green' : 'grey',
    info: { intro, sum, firstPair }
  };
}

/* ------------------------------------------------------------------------ */
/* Action card                                                               */
/* ------------------------------------------------------------------------ */

export type ActionKind = 'start' | 'next' | 'fix' | 'choose' | 'going' | 'confirm' | 'confirm-start' | 'resume' | 'done' | 'practice' | 'none';
export type ActionName =
  | 'start' | 'start-practice' | 'keep' | 'stop' | 'finish' | 'back' | 'change' | 'help' | 'back-to-course'
  | 'open-medium' | 'open-hard' | 'review-last' | 'contact-mentor' | 'confirm-start' | 'go-back';

export interface ActionButton { label: string; action: ActionName; }

export interface ActionView {
  kind: ActionKind;
  pill: { label: string; tone: Tone; icon: PillIcon };
  title: string;
  /** Plain sentence; {{ }} marks a value for the template. */
  text: string | null;
  /** Numbered steps: the Loop first, then the target. */
  steps: { loop: boolean; text: string }[] | null;
  /** Filled (recommended) button. */
  primary: ActionButton | null;
  /** Outlined button. */
  secondary: ActionButton | null;
  /** Text links under the buttons. */
  links: ActionButton[];
  /** Extra line under the box (info strip). */
  extra: string | null;
  /** A strip above the box for an event on the last quiz (timed out, retake). */
  notice: { pill: string; title: string; text: string; tone: Tone; review: CementingAttempt | null } | null;
  /** Help pop-up: the two ways, recommended first. */
  help: { title: string; text: string; recommended: boolean }[] | null;
  /** The attempt a "Review Quiz n" button opens. */
  reviewAttempt: CementingAttempt | null;
  /** The waiting moment behind a choice card. */
  moment: CementingMoment | null;
}

export type LocalChoice = CementingChoice | 'CONFIRM_START' | null;

export interface ActionContext {
  /** A "Keep going" pressed in this session (the server learns it when the next quiz starts), a Finish awaiting confirmation, or a start awaiting confirmation. */
  localChoice: LocalChoice;
  /** The other module of the subject, for the Hard / Medium interplay lines. */
  sibling: CementingCategory | null;
}

const LOOP_STEP = 'Watch the Loop video. Then go through each question you got wrong.';
const FINISH_AT_THRESHOLD_HELP = 'You finish at the threshold. You can still take the other quizzes.';
const EMPTY: Pick<ActionView, 'steps' | 'secondary' | 'links' | 'extra' | 'notice' | 'help' | 'reviewAttempt' | 'moment'> = {
  steps: null, secondary: null, links: [], extra: null, notice: null, help: null, reviewAttempt: null, moment: null
};

function lastSlot(c: CementingCategory): CementingAttempt | null {
  const attempted = attemptedSlots(c);
  return attempted.length ? attempted[attempted.length - 1] : null;
}

/** Latest finished attempt including retakes, by date. */
function latestByDate(c: CementingCategory): { attempt: CementingAttempt; retake: boolean; of: CementingAttempt } | null {
  let best: { attempt: CementingAttempt; retake: boolean; of: CementingAttempt; t: number } | null = null;
  for (const slot of c.quizzes || []) {
    if (!slot.status) { continue; }
    const candidates = [{ a: slot, retake: false }].concat((slot.retakes || []).map(r => ({ a: r, retake: true })));
    for (const x of candidates) {
      const d = x.a.finishedAt || x.a.startedAt;
      const t = d === null || d === undefined ? 0 : new Date(d).getTime();
      if (!best || t > best.t) { best = { attempt: x.a, retake: x.retake, of: slot, t }; }
    }
  }
  return best;
}

/** Questions not reached in a timed-out quiz, when the mistake breakdown carries it. */
function notReached(c: CementingCategory, quizNumber: number): number | null {
  const b = (c.l20MistakeBreakdownByQuiz || []).find(x => x.quizNumber === quizNumber);
  const n = b && b.buckets ? b.buckets['NOT_REACHED'] : undefined;
  return n === undefined || n === null ? null : n;
}

function eventNotice(c: CementingCategory): ActionView['notice'] {
  const latest = latestByDate(c);
  if (!latest) { return null; }
  const hard = isHard(c);
  if (latest.retake && latest.attempt.status === 'COMPLETED') {
    const first = latest.of;
    const firstText = first.accuracyPercent === null ? '' : (hard ? ' You made {{' + mistakes(first.mistakes || 0) + '}} in it.' : ' You scored {{' + first.accuracyPercent + '%}} in it.');
    return { pill: 'Retake', title: 'Retakes are for review only', text: 'Only your first attempt counts.' + firstText, tone: 'grey', review: latest.attempt };
  }
  const last = lastSlot(c);
  if (last && last.status === 'COMPLETED' && last.endMode === 'TIMED_OUT') {
    const missed = notReached(c, last.quizNumber);
    const countAs = hard ? 'mistakes.' : 'wrong answers.';
    return {
      pill: 'Time ran out', title: 'You ran out of time on ' + quiz(last.quizNumber),
      text: missed !== null && missed > 0 ? 'You didn\'t reach {{' + plural(missed, 'question') + '}}. They count as ' + countAs : 'The questions you didn\'t reach count as ' + countAs,
      tone: 'red', review: last
    };
  }
  return null;
}

export function deriveAction(c: CementingCategory, ctx: ActionContext = { localChoice: null, sibling: null }): ActionView {
  const view = buildAction(c, ctx);
  if (!view.notice) { view.notice = eventNotice(c); }
  return view;
}

function buildAction(c: CementingCategory, ctx: ActionContext): ActionView {
  const { summary, rules, nextQuiz } = c;
  const hard = isHard(c);
  const name = moduleName(c);
  const subject = (c.categoryKey || '').split('_')[0] || 'this subject';
  const nextNumber = nextQuiz ? nextQuiz.quizNumber : null;
  const total = (c.quizzes || []).length || summary.quizzesTotal;
  const lastQuizNumber = total;
  const l20 = l20Of(c);
  const sibling = ctx.sibling;
  const mediumPassed = !!sibling && sibling.difficulty === 'MEDIUM' && PASSED_STATES.indexOf(sibling.summary.status) >= 0;
  const nextAfter = hard ? 'Back to course' : 'Move to Hard Cementing';
  const nextAfterAction: ActionName = hard ? 'back-to-course' : 'open-hard';

  // ---- an unfinished quiz comes first (P6)
  if (nextQuiz && nextQuiz.inProgressQuizId) {
    const resumable = nextQuiz.resumable !== false;
    const progress = resumeProgress(c, nextQuiz.quizNumber);
    const done = progress ? ' (' + progress.answered + ' of ' + progress.total + ' done)' : '';
    return {
      ...EMPTY, kind: 'resume',
      pill: { label: resumable ? quiz(nextQuiz.quizNumber) + ' · in progress' : quiz(nextQuiz.quizNumber) + ' closed', tone: resumable ? 'blue' : 'red', icon: resumable ? 'play' : 'x' },
      title: resumable ? 'Pick up where you left off' : quiz(nextQuiz.quizNumber) + ' didn\'t count',
      text: resumable
        ? (progress ? 'You\'ve answered {{' + progress.answered + ' of ' + progress.total + '}} questions in ' + quiz(nextQuiz.quizNumber) + '. ' : '')
          + 'If you stay away more than 10 minutes in total, this quiz closes and won\'t count.'
        : 'You were away more than 10 minutes. Start it again; the closed attempt doesn\'t count.',
      primary: { label: resumable ? 'Resume ' + quiz(nextQuiz.quizNumber) + done : 'Start ' + quiz(nextQuiz.quizNumber) + ' again', action: 'start' }
    };
  }

  // ---- closed module
  if (!summary.open) {
    return doneAction(c, ctx, name, subject, nextAfter, nextAfterAction, mediumPassed);
  }

  // ---- a moment waiting for a choice
  const waiting = summary.waitingQuizNumber !== null && summary.waitingQuizNumber !== undefined ? momentOfQuiz(c, summary.waitingQuizNumber) : null;
  if (waiting && waiting.moment && nextNumber !== null) {
    return choiceAction(c, ctx, waiting, nextNumber, name, hard, mediumPassed);
  }

  if (!nextQuiz) {
    return {
      ...EMPTY, kind: 'none',
      pill: { label: 'No quiz available', tone: 'grey', icon: 'none' },
      title: 'Nothing to start right now',
      text: 'There is no cementing quiz available for this module at the moment.',
      primary: null
    };
  }

  const last = lastSlot(c);
  const isLast = nextQuiz.quizNumber === lastQuizNumber || summary.quizzesLeft === 1;

  // ---- nothing attempted yet
  if (!last) {
    return {
      ...EMPTY, kind: 'start',
      pill: { label: 'Before you start', tone: 'blue', icon: 'play' },
      title: 'Start with your warm-up',
      text: quiz(nextQuiz.quizNumber) + ' is a warm-up and doesn\'t count. ' + (hard ? 'Aim for {{' + orFewer(rules.threshold.perQuizMistakes) + '}}.' : 'Use it to settle in.')
        + (nextQuiz.recommendedTiming === 'RELAXED' ? ' Relaxed timing is recommended.' : ''),
      primary: { label: 'Start ' + quiz(nextQuiz.quizNumber), action: 'start' }
    };
  }

  // ---- last quiz abandoned, not the last of the module (X1)
  if (last.status === 'ABANDONED') {
    return {
      ...EMPTY, kind: 'next',
      pill: { label: quiz(last.quizNumber) + ' closed', tone: 'red', icon: 'x' },
      title: quiz(last.quizNumber) + ' didn\'t count',
      text: 'You were away more than 10 minutes. You have {{' + plural(summary.quizzesLeft, 'quiz', 'quizzes') + '}} left.',
      primary: { label: 'Start ' + quiz(nextNumber), action: 'start' }
    };
  }

  const lastMoment = momentOfQuiz(c, last.quizNumber);
  const kind = lastMoment ? lastMoment.moment : null;
  const q = questionsOf(c, last.quizNumber);
  const start: ActionButton = { label: 'Start ' + quiz(nextNumber), action: 'start' };
  const review: ActionButton = { label: 'Review ' + quiz(last.quizNumber), action: 'review-last' };

  // ---- after the warm-up (W1–W3)
  if (kind === 'WARMUP_DONE' || (summary.countedQuizzes === 1 && last.kind === 'WARMUP')) {
    const score = last.score === null ? null : last.score;
    const low = score !== null && pct(score, q) < rules.threshold.percent;
    const warmupChip = { label: '✓ Warm-up done', tone: 'green' as Tone, icon: 'none' as PillIcon };
    if (last.quizNumber > 1 && summary.abandonedQuizzes > 0) {
      return {
        ...EMPTY, kind: 'next', pill: warmupChip,
        title: quiz(last.quizNumber) + ' was your warm-up',
        text: quiz(last.quizNumber - 1) + ' closed, so ' + quiz(last.quizNumber) + ' became your warm-up. It doesn\'t count.',
        primary: start
      };
    }
    if (low && score !== null) {
      return {
        ...EMPTY, kind: 'next', pill: warmupChip,
        title: 'You finished your warm-up',
        text: hard ? 'Your {{' + mistakes(q - score) + '}} here don\'t count. Review them before ' + quiz(nextNumber) + '.'
          : 'Your {{' + pct(score, q) + '%}} here doesn\'t count. Review your mistakes before ' + quiz(nextNumber) + '.',
        primary: review, secondary: start, reviewAttempt: last
      };
    }
    return {
      ...EMPTY, kind: 'next', pill: warmupChip,
      title: 'You finished your warm-up',
      text: hard ? 'Your warm-up doesn\'t count. In ' + quiz(nextNumber) + ', make {{' + orFewer(rules.threshold.perQuizMistakes) + '}}.'
        : 'Your warm-up doesn\'t count. From ' + quiz(nextNumber) + ', your score is the average of your last two quizzes.',
      primary: start
    };
  }

  // ---- a Keep going the server already holds, with no quiz taken since (K6 / M6 / T6 without the change link)
  if (lastMoment && lastMoment.choice === 'KEEP_GOING' && lastMoment.takesChoice && kind !== 'CHECKIN_1') {
    const bar: CementingBarName = summary.bestLevel === 'BELOW_THRESHOLD' ? 'THRESHOLD' : 'MASTERY';
    return keepGoingCard(c, nextNumber, bar, kind === 'CHECKIN_BELOW_AGAIN', false);
  }

  const doneChip = { label: '✓ ' + quiz(last.quizNumber) + ' done', tone: 'green' as Tone, icon: 'none' as PillIcon };
  const bar: CementingBarName = summary.bestLevel === 'BELOW_THRESHOLD' ? 'THRESHOLD' : 'MASTERY';
  const g = goal(c, nextNumber, bar);
  const prev = prevOfTarget(c, last);
  const low = l20 ? lowQuiz(c, l20) : null;
  const unevenAtBar = !!l20 && !!low && l20.pairEven === false && low.number === l20.prevNumber
    && l20.score >= barFor(bar === 'MASTERY' ? rules.masteryTarget : rules.threshold, rules, l20.max).score;

  // ---- last quiz of the module (P5)
  if (isLast && !unevenAtBar) {
    let text: string;
    if (g.reachable) {
      text = g.verb + ' to reach ' + barName(bar) + '.';
    } else {
      const why = prev
        ? (hard ? quiz(prev.number) + ' had {{' + mistakes(questionsOf(c, prev.number) - prev.score) + '}}, so even' : 'Your ' + quiz(prev.number) + ' score ({{' + pct(prev.score, questionsOf(c, prev.number)) + '%}}) is too low, so even')
        : 'Even';
      text = why + ' a perfect ' + quiz(nextNumber) + ' can\'t reach ' + barName(bar) + '.' + (bar === 'MASTERY' ? ' You keep the threshold whatever you score.' : '');
    }
    return {
      ...EMPTY, kind: 'next',
      pill: { label: 'Last quiz', tone: 'red', icon: 'none' },
      title: 'This is your last quiz here',
      text,
      primary: start
    };
  }

  // ---- uneven pair at the bar, not a check-in (C7 / T13)
  if (unevenAtBar && l20 && low) {
    const close = bar === 'MASTERY' ? 'You\'re close to mastery. ' : 'You\'re close. ';
    return {
      ...EMPTY, kind: 'next',
      pill: doneChip,
      title: close + quiz(nextNumber) + ' decides it.',
      text: (hard ? quiz(low.number) + ' has {{' + mistakes(questionsOf(c, low.number) - low.score) + '}}, too many to count.' : 'Your ' + quiz(low.number) + ' score ({{' + pct(low.score, questionsOf(c, low.number)) + '%}}) is too low to count.')
        + ' ' + quiz(nextNumber) + ' replaces it: ' + lc(g.verb) + ' to reach ' + barName(bar) + '.',
      primary: start
    };
  }

  // ---- going for mastery but the pair dipped below the threshold (T14)
  if (bar === 'MASTERY' && l20 && levelOf(l20.score, rules, l20.max) === 'BELOW_THRESHOLD') {
    return {
      ...EMPTY, kind: 'next',
      pill: doneChip,
      title: 'Tough quiz. You keep the threshold.',
      text: lastTwo(c, l20) + '. Review ' + quiz(last.quizNumber) + ' before ' + quiz(nextNumber) + '.',
      primary: review, secondary: start, reviewAttempt: last
    };
  }

  // ---- the next quiz alone can't reach the bar (W5 / C6 / T12)
  if (!g.reachable && prev) {
    return {
      ...EMPTY, kind: 'next',
      pill: doneChip,
      title: summary.countedQuizzes === 2 ? quiz(last.quizNumber) + ' was tough. Focus on the next two.' : 'Next: ' + quiz(nextNumber),
      text: tooLowLine(c, prev.number, prev.score, nextNumber) + ' ' + twoQuizGoal(c, nextNumber, bar) + '.',
      primary: review, secondary: start, reviewAttempt: last
    };
  }

  // ---- after Quiz 2 (W4) and between check-ins (C5 / T10 / T11)
  return {
    ...EMPTY, kind: 'next',
    pill: doneChip,
    title: 'Next: ' + quiz(nextNumber),
    text: g.inQuiz + ' to reach ' + barName(bar) + '.',
    primary: start
  };
}

/** Step 2 after Keep going: the Loop, then the target (K6 / M6). */
function keepGoingCard(c: CementingCategory, nextNumber: number, bar: CementingBarName, againstAdvice: boolean, changeable: boolean): ActionView {
  const g = goal(c, nextNumber, bar);
  const target = 'Then ' + lc(g.reachable ? g.inQuiz : twoQuizGoal(c, nextNumber, bar)) + '.';
  const links: ActionButton[] = changeable ? [{ label: 'Change my choice', action: 'change' }] : [];
  if (againstAdvice) { links.push({ label: 'Contact my mentor', action: 'contact-mentor' }); }
  return {
    ...EMPTY, kind: 'fix',
    pill: againstAdvice ? { label: '↻ Keeping going', tone: 'blue', icon: 'redo' } : { label: '↗ Going for mastery', tone: 'amber', icon: 'none' },
    title: againstAdvice ? 'Your call. Make ' + quiz(nextNumber) + ' count.' : 'Fix your mistakes, then take ' + quiz(nextNumber),
    text: null,
    steps: [{ loop: true, text: LOOP_STEP }, { loop: false, text: target }],
    primary: { label: 'Start ' + quiz(nextNumber), action: 'start' },
    links
  };
}

function choiceAction(c: CementingCategory, ctx: ActionContext, waiting: CementingMoment, nextNumber: number, name: string, hard: boolean, mediumPassed: boolean): ActionView {
  const { summary, rules } = c;
  const kind = waiting.moment;
  const local = ctx.localChoice;
  const l20 = l20OfMoment(c, waiting) || l20Of(c);
  const checkinAfter = summary.nextCheckinQuizNumber;
  const pairEnd = checkinAfter && checkinAfter > nextNumber ? checkinAfter : nextNumber + 1;
  const oneLeft = summary.quizzesLeft === 1;
  const low = l20 ? lowQuiz(c, l20) : null;
  const uneven = (waiting.uneven || (l20 ? l20.pairEven === false : false)) && !!low;
  const mastery = rules.masteryTarget;
  const threshold = rules.threshold;
  const helpLink: ActionButton = { label: 'Help me decide', action: 'help' };
  const finish: ActionButton = { label: 'Finish here', action: 'stop' };

  // ---- starting the next quiz without choosing (X4)
  if (local === 'CONFIRM_START') {
    return {
      ...EMPTY, kind: 'confirm-start',
      pill: { label: 'Before ' + quiz(nextNumber), tone: 'grey', icon: 'info' },
      title: 'Keep going with ' + quiz(nextNumber) + '?',
      text: 'If you start ' + quiz(nextNumber) + ', you keep going. You can choose to stop again after Quiz {{' + pairEnd + '}}.',
      primary: { label: 'Start ' + quiz(nextNumber), action: 'confirm-start' },
      secondary: { label: 'Go back', action: 'go-back' },
      moment: waiting
    };
  }

  // ---- check-in 1: one button only (C1–C4)
  if (kind === 'CHECKIN_1') {
    const g = goal(c, nextNumber, 'THRESHOLD');
    const close = l20 ? closeTo(c, l20, threshold) : false;
    const prev = l20 && l20.number !== null && l20.quizScore !== null ? { number: l20.number, score: l20.quizScore } : null;
    let title: string; let text: string; let steps: ActionView['steps']; let pill = '⚑ Check-in 1';
    if (uneven && l20 && low) {
      pill = '⚑ Check-in 1 · One more quiz';
      title = 'One quiz is holding you back';
      text = unevenLine(c, l20, 'THRESHOLD', low, nextNumber);
      steps = [{ loop: true, text: 'Watch the Loop video and go through ' + quiz(low.number) + '\'s mistakes.' }, { loop: false, text: 'Then ' + lc(g.inQuiz) + '.' }];
    } else if (!g.reachable && prev) {
      title = 'You need two more quizzes to reach the threshold';
      text = tooLowLine(c, prev.number, prev.score, nextNumber);
      steps = [{ loop: true, text: LOOP_STEP }, { loop: false, text: 'Then ' + lc(twoQuizGoal(c, nextNumber, 'THRESHOLD')) + '.' }];
    } else {
      title = close && l20 ? (hard ? 'You\'re ' + mistakes(overBy(c, l20, threshold)) + ' away' : 'You\'re almost there') : 'Find out what\'s tripping you up';
      text = l20 ? lastTwo(c, l20) + '. ' + barLine(c, 'THRESHOLD', l20) : '';
      steps = [{ loop: true, text: LOOP_STEP }, { loop: false, text: 'Then ' + lc(g.inQuiz) + '.' }];
    }
    return {
      ...EMPTY, kind: 'fix',
      pill: { label: pill, tone: 'blue', icon: 'flag' },
      title, text, steps,
      primary: { label: 'Fix my mistakes, then take ' + quiz(nextNumber), action: 'start' },
      moment: waiting
    };
  }

  const met = kind !== 'CHECKIN_BELOW_AGAIN';

  // ---- step 2 after Keep going (T6 / K6 / M6)
  if (local === 'KEEP_GOING') {
    if (kind === 'CELEBRATE_THRESHOLD') {
      return {
        ...EMPTY, kind: 'going',
        pill: { label: '↗ Going for mastery', tone: 'amber', icon: 'none' },
        title: 'Next: ' + quiz(nextNumber) + (pairEnd > nextNumber ? ' and ' + quiz(pairEnd) : ''),
        text: (hard ? 'Make {{' + orFewer(mastery.perQuizMistakes) + '}} in each.' : 'Score {{' + mastery.percent + '%}} or more in each.') + ' You keep the threshold either way.',
        primary: { label: 'Start ' + quiz(nextNumber), action: 'start' },
        links: [{ label: 'Change my choice', action: 'change' }],
        moment: waiting
      };
    }
    const card = keepGoingCard(c, nextNumber, met ? 'MASTERY' : 'THRESHOLD', kind === 'CHECKIN_BELOW_AGAIN', true);
    card.moment = waiting;
    return card;
  }

  // ---- step 2 after Finish here (T7 / M7)
  if (local === 'STOP') {
    return {
      ...EMPTY, kind: 'confirm',
      pill: { label: 'Finish here?', tone: 'grey', icon: 'info' },
      title: met ? 'Stop at the threshold?' : 'Stop below the threshold?',
      text: 'This finishes ' + name + '. You can still take the ' + (met ? 'other ' : '') + 'quizzes.',
      primary: { label: 'Yes, finish', action: 'finish' },
      secondary: { label: 'Back', action: 'back' },
      moment: waiting
    };
  }

  // ---- step 1: choose (T1–T5)
  if (kind === 'CELEBRATE_THRESHOLD') {
    const afterBelowAgain = (c.moments || []).some(m => m.moment === 'CHECKIN_BELOW_AGAIN' && m.quizNumber < waiting.quizNumber);
    const g = goal(c, nextNumber, 'MASTERY');
    const close = l20 ? closeTo(c, l20, mastery) : false;
    const perQuiz = hard ? 'For mastery, make {{' + orFewer(mastery.perQuizMistakes) + '}} per quiz.' : 'Mastery needs {{' + mastery.percent + '%}}.';
    let title = 'You reached the threshold. Next: mastery.';
    let text = hard ? perQuiz : (l20 ? lastTwo(c, l20) + '. ' : '') + perQuiz;
    let extra: string | null = null;
    let recommendStop = false;
    if (hard && !mediumPassed && ctx.sibling) {
      title = 'You reached the Hard threshold';
      text = perQuiz;
      extra = 'You don\'t need Medium Cementing anymore.';
    } else if (oneLeft) {
      title = 'You reached the threshold. One quiz left.';
      text = g.reachable ? g.inQuiz + ' to reach mastery.' : 'You can\'t reach mastery in the one quiz left. If you stop here, you keep the threshold.';
      recommendStop = !g.reachable;
    } else if (afterBelowAgain) {
      title = 'Your hard work paid off';
      text = 'You reached the threshold. ' + perQuiz;
    } else if (close && l20) {
      title = 'You reached the threshold. Mastery is close.';
      text = lastTwo(c, l20) + '. ' + barLine(c, 'MASTERY', l20);
    }
    const go: ActionButton = { label: 'Go for mastery', action: 'keep' };
    const goHelp = {
      title: 'Go for mastery',
      text: hard ? 'Take two more quizzes with ' + orFewer(mastery.perQuizMistakes) + ' in each.' : 'Take two more quizzes and score ' + mastery.percent + '% or more in each.',
      recommended: !recommendStop
    };
    const finishHelp = { title: 'Finish here', text: FINISH_AT_THRESHOLD_HELP, recommended: recommendStop };
    return {
      ...EMPTY, kind: 'choose',
      pill: { label: '★ Threshold met', tone: 'amber', icon: 'none' },
      title, text, extra,
      primary: recommendStop ? finish : go,
      secondary: waiting.allowsStop ? (recommendStop ? go : finish) : null,
      links: [helpLink],
      moment: waiting,
      help: recommendStop ? [finishHelp, goHelp] : [goHelp, finishHelp]
    };
  }

  // ---- mastery check-in (K1–K5)
  if (kind === 'CHECKIN_MASTERY') {
    const close = l20 ? closeTo(c, l20, mastery) : false;
    const below = l20 ? levelOf(l20.score, rules, l20.max) === 'BELOW_THRESHOLD' : false;
    const g = goal(c, nextNumber, 'MASTERY');
    let pill = '⚑ Mastery check-in';
    let title: string; let text: string;
    let recommendStop = false;
    if (uneven && l20 && low) {
      pill = '⚑ Mastery check-in · One more quiz';
      title = 'One quiz is holding you back';
      text = unevenLine(c, l20, 'MASTERY', low, null);
    } else if (oneLeft) {
      title = 'One last chance at mastery';
      text = quiz(nextNumber) + ' is your last quiz. ' + (g.reachable ? g.verb + ' to reach mastery.' : 'You can\'t reach mastery in it. If you stop here, you keep the threshold.');
      recommendStop = !g.reachable;
    } else if (below && l20) {
      title = 'Your scores dropped. You keep the threshold.';
      text = lastTwo(c, l20) + '. ' + (hard ? 'Fix them' : 'Fix your mistakes') + ' with the Loop video before the next quiz.';
    } else if (close && l20) {
      title = hard ? 'You\'re ' + mistakes(overBy(c, l20, mastery)) + ' from mastery' : 'You\'re close to mastery';
      text = lastTwo(c, l20) + '. ' + barLine(c, 'MASTERY', l20);
    } else {
      title = 'Keep going for mastery?';
      text = l20 ? lastTwo(c, l20) + '. ' + barLine(c, 'MASTERY', l20) : '';
    }
    const keep: ActionButton = { label: 'Fix my mistakes and keep going', action: 'keep' };
    const keepHelp = {
      title: 'Keep going',
      text: 'Fix your mistakes with the Loop video. Then ' + lc(g.reachable ? g.inQuiz : twoQuizGoal(c, nextNumber, 'MASTERY')) + '.',
      recommended: !recommendStop
    };
    const finishHelp = { title: 'Finish here', text: FINISH_AT_THRESHOLD_HELP, recommended: recommendStop };
    return {
      ...EMPTY, kind: 'choose',
      pill: { label: pill, tone: 'blue', icon: 'flag' },
      title, text,
      primary: recommendStop ? finish : keep,
      secondary: waiting.allowsStop ? (recommendStop ? keep : finish) : null,
      links: [helpLink],
      moment: waiting,
      help: recommendStop ? [finishHelp, keepHelp] : [keepHelp, finishHelp]
    };
  }

  // ---- CHECKIN_BELOW_AGAIN: the mentor path (M1–M5), Finish here recommended
  const checkinNo = summary.checkinsSoFar || 2;
  const close = l20 ? closeTo(c, l20, threshold) : false;
  const g = goal(c, nextNumber, 'THRESHOLD');
  let pill = '⚑ Check-in ' + checkinNo;
  let title: string; let text: string; let steps: ActionView['steps'] = null;
  let mentorRecommended = true;
  if (uneven && l20 && low) {
    pill += ' · One more quiz';
    mentorRecommended = false;
    title = 'One quiz is holding you back';
    text = unevenLine(c, l20, 'THRESHOLD', low, nextNumber);
    steps = [{ loop: true, text: 'Watch the Loop video and go through ' + quiz(low.number) + '\'s mistakes.' }, { loop: false, text: 'Then ' + lc(g.inQuiz) + '.' }];
  } else if (oneLeft) {
    title = 'Stop here and talk to your mentor';
    text = (l20 ? lastTwo(c, l20, true) : 'You\'re still below the threshold') + ', and you have one quiz left.';
  } else if (checkinNo >= 3) {
    title = 'Stop here and talk to your mentor';
    text = (l20 ? lastTwo(c, l20, true) + '. ' : '') + barLine(c, 'THRESHOLD', l20);
  } else if (close && l20) {
    title = 'You\'re close. Your mentor can help you get there.';
    text = hard ? 'You\'re still {{' + mistakes(overBy(c, l20, threshold)) + '}} over the threshold after two check-ins.' : lastTwo(c, l20) + '. ' + barLine(c, 'THRESHOLD', l20);
  } else {
    title = 'Talk to your mentor now';
    text = (l20 ? lastTwo(c, l20, true) : 'You\'re still below the threshold') + '. More quizzes alone won\'t fix this. Your mentor can help.';
  }
  const keepGoing: ActionButton = { label: mentorRecommended ? (oneLeft ? 'Take ' + quiz(nextNumber) : 'Keep going') : 'Fix my mistakes, then take ' + quiz(nextNumber), action: 'keep' };
  const stopMentor: ActionButton = { label: mentorRecommended ? 'Finish here and talk to my mentor' : 'Finish here', action: 'stop' };
  const keepHelp = {
    title: 'Keep going',
    text: 'Fix your mistakes, then take ' + quiz(nextNumber) + '. You can stop again after ' + quiz(pairEnd) + '.',
    recommended: !mentorRecommended
  };
  const stopHelp = {
    title: mentorRecommended ? 'Stop and talk to your mentor (recommended)' : 'Finish here',
    text: 'You finish below the threshold. Your mentor helps you find what\'s holding you back.',
    recommended: mentorRecommended
  };
  return {
    ...EMPTY, kind: 'choose',
    pill: { label: pill, tone: mentorRecommended ? 'red' : 'blue', icon: 'flag' },
    title, text, steps,
    primary: mentorRecommended ? stopMentor : keepGoing,
    secondary: waiting.allowsStop ? (mentorRecommended ? keepGoing : stopMentor) : null,
    links: [helpLink],
    moment: waiting,
    help: mentorRecommended ? [stopHelp, keepHelp] : [keepHelp, stopHelp]
  };
}

function doneAction(c: CementingCategory, ctx: ActionContext, name: string, subject: string, nextAfter: string, nextAfterAction: ActionName, mediumPassed: boolean): ActionView {
  const { summary, rules, nextQuiz } = c;
  const hard = isHard(c);
  const l20 = l20Of(c);
  const last = lastSlot(c);
  const practiceOpen = !!nextQuiz && nextQuiz.practice && !nextQuiz.inProgressQuizId;
  const practiceLink: ActionButton[] = practiceOpen ? [{ label: 'Practice with unused quizzes', action: 'start-practice' }] : [];
  const mentor: ActionButton = { label: 'Contact my mentor', action: 'contact-mentor' };
  const back: ActionButton = { label: 'Back to course', action: 'back-to-course' };
  const next: ActionButton = { label: nextAfter, action: nextAfterAction };
  const usedUp = summary.stateReason === 'QUIZZES_USED_UP';
  const stateMoment = summary.stateQuizNumber ? momentOfQuiz(c, summary.stateQuizNumber) : null;
  const total = (c.quizzes || []).length || summary.quizzesTotal;
  const onLastQuiz = summary.stateQuizNumber === total;
  const abandonedLast = !!last && last.status === 'ABANDONED';
  const l20Line = l20 ? lastTwo(c, l20) + '.' : '';
  const practiceChip = { label: '↻ Practice quiz', tone: 'grey' as Tone, icon: 'redo' as PillIcon };

  // ---- practice after Done (E10–E12)
  if (last && last.kind === 'PRACTICE') {
    const dipped = summary.status === 'DONE_MASTERY_MET' && l20 && l20.score < barFor(rules.masteryTarget, rules, l20.max).score;
    if (summary.status === 'NOT_NEEDED') {
      return { ...EMPTY, kind: 'practice', pill: practiceChip, title: 'Practice done',
        text: 'You still don\'t need Medium.' + (l20Line ? ' ' + l20Line : ''), primary: back, links: practiceLink };
    }
    if (dipped && l20) {
      return { ...EMPTY, kind: 'practice', pill: practiceChip, title: 'A lower practice score is OK',
        text: 'You keep mastery. ' + lc(lastTwo(c, l20)) + ', so review this quiz.',
        primary: { label: 'Review this quiz', action: 'review-last' }, secondary: back, reviewAttempt: last, links: practiceLink };
    }
    return { ...EMPTY, kind: 'practice', pill: practiceChip,
      title: summary.status === 'DONE_MASTERY_MET' ? 'Practice done. You keep mastery.' : 'Practice done', text: l20Line || null, primary: back, links: practiceLink };
  }

  switch (summary.status) {
    case 'DONE_MASTERY_MET': {
      const jumped = !(c.moments || []).some(m => m.moment === 'CELEBRATE_THRESHOLD');
      const finished = hard ? 'You\'ve finished ' + subject + ' cementing.' : 'You\'ve finished Medium Cementing.';
      let title = 'You mastered ' + (hard ? 'Hard ' : 'Medium ') + subject;
      let text = (l20Line ? l20Line + ' ' : '') + finished;
      let extra: string | null = null;
      if (hard && !mediumPassed && ctx.sibling) {
        text = finished;
        extra = 'You don\'t need Medium Cementing anymore.';
      } else if (onLastQuiz) {
        title = 'You reached mastery on your last quiz';
        text = 'You\'ve finished ' + name + '.';
      } else if (jumped) {
        title = 'You went straight to mastery';
        text = hard ? 'You skipped past the threshold in one go. You\'ve finished Hard Cementing.' : text;
      }
      return {
        ...EMPTY, kind: 'done',
        pill: { label: '◆ Mastery reached', tone: 'green', icon: 'none' },
        title, text,
        primary: next, secondary: practiceOpen ? { label: 'Practice with unused quizzes', action: 'start-practice' } : null,
        extra
      };
    }
    case 'DONE_THRESHOLD_MET': {
      if (usedUp) {
        const metOnLast = !!stateMoment && stateMoment.newLevel === 'THRESHOLD_MET';
        if (abandonedLast && last) {
          return { ...EMPTY, kind: 'done', pill: { label: quiz(last.quizNumber) + ' closed', tone: 'grey', icon: 'info' }, title: quiz(last.quizNumber) + ' didn\'t count',
            text: 'You were away more than 10 minutes. You finish at: {{Threshold met}}.', primary: next };
        }
        if (metOnLast) {
          return { ...EMPTY, kind: 'done', pill: { label: '★ Threshold met · All quizzes done', tone: 'amber', icon: 'none' }, title: 'You reached the threshold on your last quiz',
            text: 'You\'ve finished ' + name + ' at the threshold.', primary: next };
        }
        return { ...EMPTY, kind: 'done', pill: { label: '✓ All quizzes done', tone: 'green', icon: 'none' }, title: 'You finished at the threshold',
          text: l20 ? lastTwo(c, l20) + '. ' + barLine(c, 'MASTERY', l20) : 'You\'ve finished ' + name + ' at the threshold.', primary: next };
      }
      return {
        ...EMPTY, kind: 'done',
        pill: { label: '✓ Done · Threshold met', tone: 'green', icon: 'none' },
        title: 'You\'ve finished ' + name,
        text: (hard ? 'You\'ve finished ' + subject + ' cementing.' : 'Next up: Hard Cementing.') + (practiceOpen ? ' You can still take the unused quizzes or retake any quiz.' : ''),
        primary: next, links: practiceLink
      };
    }
    case 'DONE_THRESHOLD_NOT_MET': {
      if (usedUp) {
        const low = l20 ? lowQuiz(c, l20) : null;
        const unevenLast = !!stateMoment && stateMoment.uneven && !!l20 && !!low;
        if (abandonedLast && last) {
          return { ...EMPTY, kind: 'done', pill: { label: quiz(last.quizNumber) + ' closed', tone: 'red', icon: 'mentor' }, title: quiz(last.quizNumber) + ' didn\'t count',
            text: 'You were away more than 10 minutes. You finish at: {{Threshold not met}}. Talk to your strategy mentor next.', primary: mentor, secondary: back };
        }
        if (unevenLast && l20 && low) {
          return { ...EMPTY, kind: 'done', pill: { label: 'All quizzes done', tone: 'red', icon: 'mentor' }, title: 'You were close, but no quizzes are left',
            text: unevenLine(c, l20, 'THRESHOLD', low, null), primary: mentor, secondary: back };
        }
        return { ...EMPTY, kind: 'done', pill: { label: 'All quizzes done', tone: 'red', icon: 'mentor' }, title: 'Next: talk to your strategy mentor',
          text: l20 ? lastTwo(c, l20) + '. ' + barLine(c, 'THRESHOLD', l20) : 'You didn\'t reach the threshold.',
          primary: mentor, secondary: back };
      }
      return {
        ...EMPTY, kind: 'done',
        pill: { label: 'Done · Threshold not met', tone: 'red', icon: 'mentor' },
        title: 'Next: talk to your strategy mentor',
        text: 'Your mentor will go through your quizzes with you.',
        primary: mentor, secondary: back, links: practiceLink
      };
    }
    default: { // NOT_NEEDED (H6 / H7)
      const fromNotMet = summary.stateReason === 'HARD_PASSED' && (c.moments || []).some(m => m.statusAfter === 'DONE_THRESHOLD_NOT_MET');
      return {
        ...EMPTY, kind: 'done',
        pill: { label: 'Not needed', tone: 'green', icon: 'check' },
        title: fromNotMet ? 'You don\'t need Medium anymore' : 'You don\'t need Medium Cementing',
        text: fromNotMet ? 'You passed Hard, so you can skip Medium.' : 'You passed Hard. You can still practice with Medium quizzes.',
        primary: practiceOpen ? { label: 'Practice a Medium quiz', action: 'start-practice' } : back,
        secondary: practiceOpen ? back : null
      };
    }
  }
}

/* ------------------------------------------------------------------------ */
/* Hard / Medium interplay notices (H1–H5)                                   */
/* ------------------------------------------------------------------------ */

export interface HardNotice {
  pill: string;
  tone: Tone;
  title: string;
  text: string;
  primary: ActionButton | null;
  secondary: ActionButton | null;
}

/** Notice at the top of the Hard panel while Medium isn't done, or once Hard passed (spec section 9). */
export function deriveHardNotice(hard: CementingCategory | null, medium: CementingCategory | null): HardNotice | null {
  if (!hard || hard.difficulty !== 'HARD' || !medium) { return null; }
  const mediumPassed = PASSED_STATES.indexOf(medium.summary.status) >= 0;
  const mediumDone = !medium.summary.open;
  if (hard.summary.status === 'NOT_NEEDED') { return null; }
  if (PASSED_STATES.indexOf(hard.summary.status) >= 0) {
    return mediumPassed ? null : {
      pill: 'ⓘ Medium not needed', tone: 'grey', title: 'You passed Hard, so you can skip Medium', text: 'You can still practice with Medium quizzes.', primary: null, secondary: null
    };
  }
  if (!hard.summary.open || mediumPassed) { return null; }
  // The warm-up quiz's own questions (a legacy RC / MSR quiz can be 6).
  const warmupSlot = (hard.quizzes || []).find(a => a.kind === 'WARMUP');
  const q = warmupSlot ? questionsOf(hard, warmupSlot.quizNumber) : (hard.rules.questionsPerQuiz || 10);
  const pctNeeded = hard.rules.hardWarmupPct === null || hard.rules.hardWarmupPct === undefined ? 60 : hard.rules.hardWarmupPct;
  const warmup = hard.summary.warmupScore;
  const warmedUp = warmup !== null && warmup !== undefined;
  const warmupGood = warmedUp && pct(warmup, q) >= pctNeeded;
  // H3 / H4 talk about the warm-up, so they show only while it is the latest counted quiz.
  const pastWarmup = warmedUp && hard.summary.countedQuizzes > 1;
  if (warmupGood) {
    return pastWarmup ? null : { pill: 'ⓘ Medium isn\'t done yet', tone: 'grey', title: 'Great Hard warm-up', text: 'You made only {{' + mistakes(q - warmup) + '}}. Keep going with Hard.', primary: null, secondary: null };
  }
  const goMedium: ActionButton = { label: 'Go to Medium Cementing', action: 'open-medium' };
  if (medium.summary.status === 'DONE_THRESHOLD_NOT_MET') {
    return { pill: 'Not recommended yet', tone: 'red', title: 'Talk to your mentor before Hard', text: 'You haven\'t reached the threshold in Medium yet.',
      primary: { label: 'Contact my mentor', action: 'contact-mentor' }, secondary: { label: 'Start Hard anyway', action: 'start' } };
  }
  if (warmedUp) {
    return pastWarmup ? null : { pill: 'Finish Medium Cementing first', tone: 'red', title: 'Do Medium first',
      text: 'You made {{' + mistakes(q - warmup) + '}} in the warm-up. Finishing Medium first will make Hard easier.', primary: goMedium, secondary: { label: 'Continue Hard', action: 'start' } };
  }
  return { pill: 'Finish Medium Cementing first', tone: 'red', title: 'Do Medium first', text: 'Hard builds on Medium. You can still start Hard if you want.',
    primary: goMedium, secondary: mediumDone ? null : { label: 'Start Hard anyway', action: 'start' } };
}

/* ------------------------------------------------------------------------ */
/* Journey header (execution tracker)                                        */
/* ------------------------------------------------------------------------ */

export type JourneyStatus = 'new' | 'started' | 'completed';

export interface JourneyHeaderView {
  status: JourneyStatus;
  statusLabel: string;
  l20Percent: number | null;
}

export function deriveJourneyHeader(category: CementingCategory | null): JourneyHeaderView {
  if (!category) { return { status: 'new', statusLabel: 'Not started', l20Percent: null }; }
  const s = category.summary;
  const started = s.countedQuizzes > 0 || s.abandonedQuizzes > 0 || attemptedSlots(category).length > 0;
  const status: JourneyStatus = s.countsAsComplete ? 'completed' : started ? 'started' : 'new';
  let statusLabel: string;
  switch (s.status) {
    case 'THRESHOLD_MET_GOING_FOR_MASTERY': statusLabel = 'Threshold met · going for mastery'; break;
    case 'DONE_MASTERY_MET': statusLabel = 'Done · Mastery met'; break;
    case 'DONE_THRESHOLD_MET': statusLabel = 'Done · Threshold met'; break;
    case 'DONE_THRESHOLD_NOT_MET': statusLabel = 'Done · Threshold not met'; break;
    case 'NOT_NEEDED': statusLabel = 'Not needed (passed Hard)'; break;
    default: statusLabel = started ? 'In progress' : 'Not started';
  }
  return { status, statusLabel, l20Percent: s.latestL20 ? s.latestL20.percent : null };
}
